/*
 * Article page — auto table of contents, scroll spy, reading progress bar and copy link.
 * Scoped to [data-article-post] so multiple sections never collide.
 */
(function () {
  const ROOT_SELECTOR = '[data-article-post]';
  const INITIALISED = 'mraArticleReady';

  function slugify(text, index) {
    const slug = text
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}\s-]/gu, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60);

    return slug ? slug : 'section-' + (index + 1);
  }

  function buildToc(root) {
    const content = root.querySelector('[data-mra-content]');
    const nav = root.querySelector('[data-mra-toc]');
    const list = root.querySelector('[data-mra-toc-list]');

    if (!content || !nav || !list) return;

    const selector = nav.dataset.mraTocDepth === 'h2h3' ? 'h2, h3' : 'h2';
    const headings = Array.prototype.filter.call(
      content.querySelectorAll(selector),
      (heading) => heading.textContent.trim() !== ''
    );

    if (headings.length < 2) return;

    const used = {};
    const items = [];

    headings.forEach((heading, index) => {
      if (!heading.id) {
        let id = slugify(heading.textContent, index);
        if (used[id]) {
          used[id] += 1;
          id = id + '-' + used[id];
        } else {
          used[id] = 1;
        }
        heading.id = id;
      }

      const item = document.createElement('li');
      item.className = 'mra-toc__item mra-toc__item--' + heading.tagName.toLowerCase();

      const link = document.createElement('a');
      link.className = 'mra-toc__link';
      link.href = '#' + heading.id;
      link.textContent = heading.textContent.trim();

      item.appendChild(link);
      list.appendChild(item);
      items.push({ item: item, heading: heading });
    });

    nav.hidden = false;
    watchHeadings(items);
  }

  /*
   * Scroll spy. This reads positions on scroll rather than using IntersectionObserver: the
   * observer only fired while a heading was crossing the band, so a long section between two
   * headings left the highlight on whatever happened to cross last — and nothing was active at
   * all until the reader passed the first heading. Here the active item is simply the last
   * heading above the reading line, which is always defined once the article starts.
   */
  function watchHeadings(items) {
    const list = items[0].item.parentNode;

    // --mra-anchor-offset is a calc() on the live header height, so read the resolved
    // scroll-margin-top off a heading instead of parsing the custom property. The extra few
    // pixels make sure a heading that was just jumped to counts as active.
    const readingLine = () => {
      const offset = parseFloat(getComputedStyle(items[0].heading).scrollMarginTop);
      return Number.isNaN(offset) ? 130 : offset + 4;
    };

    let activeItem = null;
    let ticking = false;

    // The list has its own scrollbar, so the active row can sit outside it. Nudge it back into
    // view, but only within the list — scrollIntoView would drag the whole page with it.
    const revealInList = (item) => {
      if (!list || list.scrollHeight <= list.clientHeight) return;

      const top = item.offsetTop - list.offsetTop;
      const bottom = top + item.offsetHeight;

      if (top < list.scrollTop) {
        list.scrollTop = top - 8;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight + 8;
      }
    };

    const setActive = (item) => {
      if (item === activeItem) return;
      activeItem = item;
      items.forEach((entry) => entry.item.classList.toggle('is-active', entry.item === item));
      if (item) revealInList(item);
    };

    const update = () => {
      ticking = false;

      const line = readingLine();
      let current = items[0];

      items.forEach((entry) => {
        if (entry.heading.getBoundingClientRect().top - line <= 0) current = entry;
      });

      // Past the end of the article the last heading stays lit rather than the first.
      const last = items[items.length - 1];
      const documentEnd = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
      if (documentEnd) current = last;

      setActive(current ? current.item : null);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initProgress(root) {
    const bar = root.querySelector('[data-mra-progress]');
    const content = root.querySelector('[data-mra-content]');

    if (!bar || !content) return;

    let ticking = false;

    const update = () => {
      ticking = false;

      const rect = content.getBoundingClientRect();
      const scrolled = window.innerHeight - rect.top;
      const total = rect.height + window.innerHeight * 0.4;
      let ratio = total > 0 ? scrolled / total : 0;

      if (ratio < 0) ratio = 0;
      if (ratio > 1) ratio = 1;

      bar.style.transform = 'scaleX(' + ratio + ')';
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initCopy(root) {
    root.querySelectorAll('[data-mra-copy]').forEach((button) => {
      button.addEventListener('click', () => {
        const url = button.getAttribute('data-mra-copy-url') || window.location.href;

        const done = () => {
          button.classList.add('is-copied');
          window.setTimeout(() => button.classList.remove('is-copied'), 2000);
        };

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(url).then(done).catch(() => window.prompt('', url));
          return;
        }

        const field = document.createElement('input');
        field.value = url;
        field.setAttribute('readonly', 'readonly');
        field.style.position = 'absolute';
        field.style.left = '-9999px';
        document.body.appendChild(field);
        field.select();

        try {
          document.execCommand('copy');
          done();
        } catch (error) {
          window.prompt('', url);
        }

        document.body.removeChild(field);
      });
    });
  }


  function placeTocOnMobile(root) {
    const nav = root.querySelector('[data-mra-toc]');
    const header = root.querySelector('.mra-head');
    if (!nav || !header) return;

    const originalParent = nav.parentNode;
    const originalNextSibling = nav.nextSibling;
    const mobile = window.matchMedia('(max-width: 749px)');

    function updateLocation() {
      if (mobile.matches) {
        header.after(nav);
      } else {
        originalParent.insertBefore(nav, originalNextSibling);
      }
    }

    updateLocation();
    mobile.addEventListener('change', updateLocation);
  }

  function initRoot(root) {
    if (!root || root.dataset[INITIALISED] === 'true') return;
    root.dataset[INITIALISED] = 'true';

    buildToc(root);
    placeTocOnMobile(root);
    initProgress(root);
    initCopy(root);
  }


  function init(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
